import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { genislikKaydet, genislikleriOku, KOLON_SIFIRLA_OLAYI, kolonGenislikAnahtari, otomatikGenislik, sinirla } from './kolonGenislik'

/**
 * Liste ekranlarında kolon başlığına tıklayarak sıralama + Excel gibi kolon genişliği (16.10.2026).
 *
 * Her ekranda tekrar yazılan "tıklanan kolonu bul, yönü çevir, ilk sayfaya dön"
 * mantığı burada tek yerde tutulur. Kullanımı:
 *
 *   const { thProps, sortArrow } = useSortableColumns(sortableColumns, filter, getData)
 *   ...
 *   <th {...thProps(item.name)}>{item.title}{sortArrow(item.name)}</th>
 *
 * Kolon adları backend'deki sıralama eşlemesiyle (SortMap) aynı olmalıdır.
 *
 * Kolon genişliği (Burak, 16.10.2026): başlığın sağ kenarı sürüklenerek genişlik ayarlanır, kenara çift tıklayınca
 * içeriğe göre açılır. Genişlikler ekran (adres) başına tarayıcıda saklanır; Kolon Seçici "Varsayılana dön" siler.
 * Ayar yokken kolon içeriğe göre açılır, en fazla 250 px; uzun metin "…" ile kısalır, üzerine gelince tamamı görünür.
 * Tutamaç sortArrow ile başlığa eklendiği için ekranlarda değişiklik gerekmez.
 */
export interface SortableFilter {
    sortColumn?: string
    sortDirection?: string
    currentPage?: number
    skip?: number
}

// Sürükleme bittikten hemen sonra başlığa düşen tıklama sıralama yapmasın.
let sonSurukleme = 0;

export function useSortableColumns<TFilter extends SortableFilter>(
    sortableColumns: string[],
    filter: TFilter,
    getData: (_filter?: TFilter) => void
) {
    const anahtar = kolonGenislikAnahtari(window.location.pathname);
    const [genislikler, setGenislikler] = useState<Record<string, number>>(() => genislikleriOku(anahtar));

    // Kolon Seçici "Sıfırla": genişlikler silinir, tablolar içeriğe göre düzene döner.
    useEffect(() => {
        const sifirla = () => {
            setGenislikler({});
            document.querySelectorAll<HTMLTableElement>('table.kolon-sabit').forEach(tablo => {
                tablo.classList.remove('kolon-sabit');
                tablo.style.tableLayout = '';
                tablo.style.width = '';
                tablo.style.minWidth = '';
                tablo.querySelectorAll<HTMLTableCellElement>('thead > tr:first-child > th').forEach(th => { th.style.width = ''; });
            });
        };

        window.addEventListener(KOLON_SIFIRLA_OLAYI, sifirla);
        return () => window.removeEventListener(KOLON_SIFIRLA_OLAYI, sifirla);
    }, []);

    // Aynı kolona tekrar tıklanırsa yön değişir; farklı kolona tıklanırsa artan sırayla başlar.
    const changeSort = (columnName: string) => {
        if (!sortableColumns.includes(columnName))
            return

        if (Date.now() - sonSurukleme < 300)
            return

        var direction = (filter.sortColumn == columnName && filter.sortDirection == 'asc') ? 'desc' : 'asc'

        getData({ ...filter, sortColumn: columnName, sortDirection: direction, currentPage: 1, skip: 0 })
    }

    /**
     * Tabloyu sabit düzene alır: her başlığın şu anki genişliği (ya da kayıtlı genişlik) yazılır. Sabit düzende
     * hücreler başlık genişliğine uyar, taşan metin "…" olur.
     */
    const sabitle = useCallback((tablo: HTMLTableElement, kayitli: Record<string, number>) => {
        const basliklar = Array.from(tablo.querySelectorAll<HTMLTableCellElement>('thead > tr:first-child > th'));
        const olculen = basliklar.map(th => {
            const ad = th.dataset.kolon;
            return ad && kayitli[ad] ? kayitli[ad] : th.getBoundingClientRect().width;
        });

        basliklar.forEach((th, i) => { th.style.width = Math.round(olculen[i]) + 'px'; });
        tablo.style.width = Math.round(olculen.reduce((a, b) => a + b, 0)) + 'px';
        tablo.style.minWidth = '100%';
        tablo.style.tableLayout = 'fixed';
        tablo.classList.add('kolon-sabit');
    }, []);

    /** Tabloya bir kez: ayar sınıfı, kayıtlı genişlik varsa sabit düzen, taşan hücreye tam metin ipucu. */
    const tabloyuHazirla = useCallback((th: HTMLTableCellElement | null) => {
        const tablo = th?.closest('table') as HTMLTableElement | null;

        if (!tablo)
            return;

        if (!tablo.classList.contains('kolon-ayarli')) {
            tablo.classList.add('kolon-ayarli');
            tablo.addEventListener('mouseover', e => {
                const td = (e.target as HTMLElement)?.closest?.('td') as HTMLTableCellElement | null;

                if (td && !td.title && td.scrollWidth > td.clientWidth + 1)
                    td.title = td.innerText.trim();
            });
        }

        if (Object.keys(genislikler).length > 0 && !tablo.classList.contains('kolon-sabit'))
            requestAnimationFrame(() => sabitle(tablo, genislikler));
    }, [genislikler, sabitle]);

    const kaydet = (tablo: HTMLTableElement) => {
        const yeni: Record<string, number> = {};

        tablo.querySelectorAll<HTMLTableCellElement>('thead > tr:first-child > th[data-kolon]').forEach(th => {
            yeni[th.dataset.kolon!] = Math.round(th.getBoundingClientRect().width);
        });

        genislikKaydet(anahtar, yeni);
        setGenislikler(yeni);
    }

    const tutamacBasildi = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const th = (e.currentTarget as HTMLElement).closest('th') as HTMLTableCellElement;
        const tablo = th.closest('table') as HTMLTableElement;
        const tutamac = e.currentTarget as HTMLSpanElement;

        if (!tablo.classList.contains('kolon-sabit'))
            sabitle(tablo, genislikler);

        const basX = e.clientX;
        const basGenislik = th.getBoundingClientRect().width;
        const basTablo = tablo.getBoundingClientRect().width;
        tutamac.classList.add('aktif');
        document.body.classList.add('kolon-surukleniyor');

        const hareket = (m: MouseEvent) => {
            const yeni = sinirla(basGenislik + m.clientX - basX);
            th.style.width = yeni + 'px';
            tablo.style.width = Math.round(basTablo + yeni - basGenislik) + 'px';
        };

        const birak = () => {
            document.removeEventListener('mousemove', hareket);
            document.removeEventListener('mouseup', birak);
            tutamac.classList.remove('aktif');
            document.body.classList.remove('kolon-surukleniyor');
            sonSurukleme = Date.now();
            kaydet(tablo);
        };

        document.addEventListener('mousemove', hareket);
        document.addEventListener('mouseup', birak);
    }

    // Kenara çift tıklama: kolon içeriğe göre açılır (Excel gibi).
    const tutamacCiftTiklandi = (e: React.MouseEvent<HTMLSpanElement>) => {
        e.preventDefault();
        e.stopPropagation();

        const th = (e.currentTarget as HTMLElement).closest('th') as HTMLTableCellElement;
        const tablo = th.closest('table') as HTMLTableElement;

        if (!tablo.classList.contains('kolon-sabit'))
            sabitle(tablo, genislikler);

        // Tablo genişliği başlık değişmeden ölçülür; sonra ölçülürse sabit düzen farkı zaten eklemiş olur.
        const eski = th.getBoundingClientRect().width;
        const basTablo = tablo.getBoundingClientRect().width;
        const yeni = otomatikGenislik(tablo, th);
        th.style.width = yeni + 'px';
        tablo.style.width = Math.round(basTablo + yeni - eski) + 'px';
        sonSurukleme = Date.now();
        kaydet(tablo);
    }

    // Sıralanabilen kolon başlığı tıklanabilir görünür; bütün başlıklar genişlik ayarına katılır.
    const thProps = (columnName: string) => {
        const ortak = {
            'data-kolon': columnName,
            ref: tabloyuHazirla,
            style: genislikler[columnName] ? { width: genislikler[columnName] + 'px' } : undefined,
        };

        if (!sortableColumns.includes(columnName))
            return ortak

        return {
            ...ortak,
            className: 'actionIcon',
            title: 'Sıralamak için tıklayın',
            onClick: () => changeSort(columnName)
        }
    }

    // Seçili kolonun yanında yön oku + sağ kenarda genişlik tutamacı.
    const sortArrow = (columnName: string): ReactNode => {
        const ok = filter.sortColumn != columnName ? null : filter.sortDirection == 'desc' ? ' ▼' : ' ▲';

        return (
            <>
                {ok}
                <span
                    className='kolon-tutamac'
                    title='Genişliği ayarlamak için sürükleyin, içeriğe göre açmak için çift tıklayın'
                    onMouseDown={tutamacBasildi}
                    onDoubleClick={tutamacCiftTiklandi}
                    onClick={e => e.stopPropagation()}
                />
            </>
        )
    }

    return { changeSort, thProps, sortArrow }
}
